import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useDeleteApiKey } from '../../hooks/useApiKeys';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';

const DeleteApiKeyModal = ({ isOpen, onClose, apiKey }) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const deleteApiKey = useDeleteApiKey();
    const { t } = useLanguage();

    const handleDelete = async () => {
        setDeleteError('');
        setIsDeleting(true);
        try {
            await deleteApiKey.mutateAsync({ id: apiKey.id });
            onClose();
        } catch (error) {
            console.error('Error deleting API key:', error);
            setDeleteError(error.message || t(KEYS.API_KEY_DELETE_ERROR));
        } finally {
            setIsDeleting(false);
        }
    };

    const handleClose = () => {
        if (!isDeleting) {
            setDeleteError('');
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t(KEYS.API_KEY_DELETE_TITLE)}
            subtitle={t(KEYS.COMMON_CANNOT_UNDO)}
            size="small"
            footer={
                <>
                    <Button variant="outline" onClick={handleClose} disabled={isDeleting}>
                        {t(KEYS.COMMON_CANCEL)}
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? t(KEYS.COMMON_DELETING) : t(KEYS.COMMON_DELETE)}
                    </Button>
                </>
            }
        >
            {deleteError && (
                <div className="p-3 mb-4 bg-status-overdue-bg text-status-overdue rounded text-sm">
                    {deleteError}
                </div>
            )}

            <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-status-overdue-bg rounded border border-status-overdue/20">
                    <AlertTriangle className="w-5 h-5 text-status-overdue flex-shrink-0 mt-0.5" />
                    <p className="text-text-primary font-medium">
                        Delete API key <strong>{apiKey?.name}</strong>?
                    </p>
                </div>

                <p className="text-sm text-text-muted">
                    {t(KEYS.API_KEY_DELETE_BODY)}
                </p>
            </div>
        </Modal>
    );
};

export default DeleteApiKeyModal;
